import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Zap,
  Star,
  Footprints,
  Waves,
  CalendarCheck,
  MessageCircle,
  Phone,
  Award,
  ShieldCheck,
  HeartHandshake,
  Stethoscope,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import services from "@/data/services.json";
import categories from "@/data/categories.json";
import { SITE, waHref } from "@/data/site";

const CAT_ORDER = ["cilt-bakimi", "epilasyon", "podoloji", "tirnak-islemleri", "bolgesel-sikilasma", "kalici-makyaj"];

// Category presentation — icon + cover + short intro paragraph (natural Turkish, not marketing fluff).
const COVERS = {
  "cilt-bakimi": {
    icon: Sparkles,
    image: "upload/product/650x650/hydrafacial-cilt-bakimi-20220818100600.jpg",
    hub: "/cilt-bakimi",
    tagline: "HydraFacial, kolajen, algae, pink glow, akne-skar ve daha fazlası",
    intro:
      "Cildinizin ihtiyacına göre planlanan medikal cilt bakımı seansları. HydraFacial'dan kolajen terapiye, akne-skar bakımından pink glow ışıltı protokolüne kadar farklı ihtiyaçlar için ayrı programlar sunuyoruz.",
  },
  epilasyon: {
    icon: Zap,
    image: "upload/product/650x650/lazer-epilasyon-1-gorsel-20220822142450.jpg",
    hub: null,
    tagline: "Champion multi hibrit lazer, iğneli epilasyon ve erkek epilasyon",
    intro:
      "Kadın ve erkek her cinsiyette, tüm cilt tiplerinde uygulanabilen lazer epilasyon programları. Champion multi hibrit teknolojisiyle konforlu, iğneli epilasyonla açık renk tüylere kadar geniş bir yelpaze.",
  },
  "kalici-makyaj": {
    icon: Star,
    image: "upload/product/650x650/kil-teknigi-kas-20220904195058.jpg",
    hub: null,
    tagline: "Microblading, kalıcı dipliner, dudak renklendirme, ipek kirpik",
    intro:
      "Sabahları rutininizi kolaylaştıran, doğal bir görünüm sunan kalıcı makyaj uygulamaları. Kaş, göz ve dudak için ayrı ayrı yüz analizi ve renk seçimi ile hazırlanan seans planları.",
  },
  podoloji: {
    icon: Footprints,
    image: "upload/product/650x650/refleksoloji-ankara-20250225132538.jpg",
    hub: null,
    tagline: "Batık tırnak, nasır, mantar, plantar siğil, medikal ayak bakımı",
    intro:
      "Ankara Üniversitesi Podoloji programı mezunu Podolog Serdar Ceylan ve ekibimizin uyguladığı medikal ayak bakımı hizmetleri. Batık tırnak, kalınlaşmış tırnak, topuk çatlağı ve mantar görünümlü tırnaklarda klinik çözümler.",
  },
  "tirnak-islemleri": {
    icon: Footprints,
    image: "upload/tirnak-islemleri-kapak.webp",
    hub: null,
    tagline: "Protez tırnak, nail art, manikür ve pedikür uygulamaları",
    intro:
      "Ellerinizin ve ayaklarınızın estetik, bakımlı ve şık görünmesi için protez tırnak, nail art, manikür ve pedikür alanında profesyonel uygulamalar.",
  },
  "bolgesel-sikilasma": {
    icon: Waves,
    image: "upload/product/650x650/1-20230510122131.jpg",
    hub: null,
    tagline: "G5 vibrasyon masajı ve EMS ile bölgesel sıkılaşma",
    intro:
      "Bölgesel yağlanma ve selülit görünümüne yönelik G5 vibrasyon masajı ve EMS kas artırma seansları. Kişiye özel seans sayısı ve haftalık planlama ile yürütülür.",
  },
};

function PageHero() {
  return (
    <section className="pb-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="chip-green mb-4">Hizmetlerimiz</p>
      <h1 className="font-display text-4xl md:text-6xl text-[#0B1B33] leading-[1.05] max-w-3xl mb-6 tracking-tight">
        Kadın &amp; erkek için güzellik ve bakım hizmetlerimiz
      </h1>
      <p className="text-[#4B5A6E] max-w-2xl leading-relaxed text-base md:text-lg">
        MediEste&apos;de tüm hizmetler 5 ana kategoride toplanır. Aşağıdaki her kategori kısa bir tanıtım, öne çıkan uygulamalar ve doğrudan hızlı erişim için hazırlandı — aradığınız hizmete tek sayfada ulaşabilirsiniz.
      </p>
    </section>
  );
}

/**
 * Category jump list — sticky-ish anchor chips at the top so users can jump straight to a category.
 */
function CategoryQuickNav() {
  return (
    <section className="pb-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="cat-quicknav">
      <div className="flex flex-wrap gap-2 md:gap-3">
        {CAT_ORDER.map((key) => {
          const c = categories[key];
          const cv = COVERS[key];
          if (!c || !cv) return null;
          const Icon = cv.icon;
          const count = services.filter((s) => s.category === key).length;
          return (
            <a
              key={key}
              href={`#cat-${key}`}
              data-testid={`quicknav-${key}`}
              className="group inline-flex items-center gap-2 h-11 pl-3 pr-4 rounded-full bg-white border border-[#E5EAF0] hover:border-[#10B981]/60 hover:shadow-sm transition-all"
            >
              <span className="h-7 w-7 rounded-full bg-[#EEF6F1] text-[#10B981] flex items-center justify-center">
                <Icon size={14} />
              </span>
              <span className="text-sm text-[#0B1B33] font-semibold">{c.label}</span>
              <span className="text-[10px] tracking-[0.14em] uppercase text-[#7C8CA0] font-bold ml-1">
                {count}
              </span>
            </a>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Renders each category as its own section: intro on the left, service cards on the right.
 * On mobile the intro stacks above the cards.
 */
function CategorySections({ onFilter }) {
  return (
    <div data-testid="category-sections">
      {CAT_ORDER.map((key, idx) => {
        const c = categories[key];
        const cv = COVERS[key];
        if (!c || !cv) return null;
        const Icon = cv.icon;
        const list = services.filter((s) => s.category === key);
        const preview = list.slice(0, 3);
        const zebra = idx % 2 === 1;

        return (
          <section
            key={key}
            id={`cat-${key}`}
            data-testid={`cat-section-${key}`}
            className={`py-16 md:py-20 ${zebra ? "bg-[#F5F7FA] border-y border-[#E5EAF0]" : "bg-white"}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[minmax(0,320px)_1fr] gap-8 lg:gap-12">
              {/* Category intro column */}
              <header className="lg:sticky lg:top-28 lg:self-start">
                <div className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-white border border-[#E5EAF0] mb-4 shadow-sm">
                  <Icon size={14} className="text-[#10B981]" aria-hidden="true" />
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#0B1B33] font-bold">
                    {list.length} Hizmet
                  </span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl text-[#0B1B33] tracking-tight leading-tight mb-4">
                  {c.label}
                </h2>
                <p className="text-[#4B5A6E] leading-relaxed mb-6">{cv.intro}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => onFilter(key)}
                    className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-[#0E2B4F] text-white text-xs tracking-[0.16em] uppercase font-semibold hover:bg-[#143a68] transition-colors"
                    data-testid={`cat-section-cta-${key}`}
                  >
                    Tümünü Gör <ArrowRight size={12} />
                  </button>
                  {cv.hub ? (
                    <Link
                      to={cv.hub}
                      className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full border border-[#D1D9E4] text-[#0B1B33] text-xs tracking-[0.16em] uppercase font-semibold hover:border-[#10B981]/60 hover:text-[#10B981] transition-colors"
                    >
                      Rehber
                    </Link>
                  ) : null}
                </div>
              </header>

              {/* Service preview grid */}
              <div className="min-w-0">
                {preview.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                    {preview.map((s, i) => (
                      <Link
                        key={s.slug}
                        to={`/${s.slug}`}
                        data-testid={`cat-preview-${key}-${s.slug}`}
                        className="group relative block overflow-hidden aspect-[3/4] rounded-2xl medi-card"
                      >
                        {s.image ? (
                          <img
                            src={`/${s.image}`}
                            alt={s.title}
                            loading={idx === 0 && i < 3 ? "eager" : "lazy"}
                            decoding="async"
                            className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                          />
                        ) : (
                          <div className="w-full h-full bg-[#F5F7FA]" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/85 via-[#0B1B33]/25 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-3 md:p-4">
                          <h3 className="font-display text-sm md:text-base text-white leading-tight font-medium">{s.title}</h3>
                          <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-[#A8C520] font-bold mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            Keşfet <ArrowRight size={10} />
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#7C8CA0]">Bu kategoride henüz hizmet bulunmuyor.</p>
                )}

                {list.length > preview.length ? (
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={() => onFilter(key)}
                      className="inline-flex items-center gap-1.5 text-sm text-[#0E2B4F] font-semibold uline-link"
                      data-testid={`cat-more-${key}`}
                    >
                      {c.label} kategorisindeki {list.length} hizmetin tamamı →
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}

/**
 * Quick appointment section — always rendered at the bottom of both the overview and the filtered mode.
 */
function QuickAppointmentBlock() {
  return (
    <section
      data-testid="services-quick-appointment"
      className="py-20 md:py-24 bg-gradient-to-br from-[#0E2B4F] via-[#123564] to-[#0E2B4F] relative overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#10B981]/20 blur-3xl" aria-hidden="true" />
      <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#2C6BB0]/25 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-[11px] tracking-[0.24em] uppercase text-[#A8C520] font-bold mb-4">
          Hızlı Randevu
        </p>
        <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight leading-tight mb-5">
          Hangi Hizmetin Sizin İçin Uygun Olduğuna Beraber Karar Verelim
        </h2>
        <p className="text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
          İlk görüşmede kısa bir analizle ihtiyacınıza göre bakım planı oluşturuyoruz. Bağlıca / Etimesgut&apos;taki kliniğimizden veya WhatsApp üzerinden dilediğiniz zaman ulaşabilirsiniz.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/hizli-randevu"
            className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-[#10B981] text-white text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#059669] transition-colors"
            data-testid="services-quick-cta-appointment"
          >
            <CalendarCheck size={14} /> Hızlı Randevu
          </Link>
          <a
            href={waHref("Merhaba, hizmetleriniz hakkında bilgi almak istiyorum.")}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-white text-[#0E2B4F] text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#F5F7FA] transition-colors"
            data-testid="services-quick-cta-whatsapp"
          >
            <MessageCircle size={14} /> WhatsApp
          </a>
          <a
            href={`tel:${SITE.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full border-2 border-white/60 text-white text-xs tracking-[0.18em] uppercase font-semibold hover:bg-white hover:text-[#0E2B4F] transition-colors"
            data-testid="services-quick-cta-call"
          >
            <Phone size={14} /> {SITE.phone}
          </a>
        </div>
      </div>
    </section>
  );
}

function AboutUsBlock() {
  const stats = [
    { icon: Award, label: "20+ Yıl", desc: "Klinik uygulama deneyimi" },
    { icon: ShieldCheck, label: "Sertifikalı Kadro", desc: "MEB Onaylı Usta Öğretici + MYK Belgeli" },
    { icon: Stethoscope, label: "Klinik Standart", desc: "Steril, hijyen odaklı uygulama" },
    { icon: HeartHandshake, label: "Kişiye Özel Plan", desc: "İlk görüşmede ücretsiz analiz" },
  ];
  return (
    <section data-testid="services-about" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="relative">
          <div className="absolute -top-6 -left-6 h-24 w-24 rounded-full bg-[#10B981]/15 blur-2xl pointer-events-none" aria-hidden="true" />
          <div className="absolute -bottom-6 -right-6 h-32 w-32 rounded-full bg-[#2C6BB0]/15 blur-3xl pointer-events-none" aria-hidden="true" />
          <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-[4/5] max-w-md">
            <img
              src="/upload/team/sevda-karatas.jpg"
              alt="MediEste ekibi"
              loading="lazy"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/45 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#A8C520] mb-1">MediEste Ekibi</p>
              <p className="font-display text-2xl leading-tight">Uzman Estetisyen Sevda Karataş &amp; Podolog Serdar Ceylan</p>
            </div>
          </div>
        </div>

        <div>
          <p className="chip-green mb-4">Hakkımızda</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#0B1B33] leading-[1.05] tracking-tight mb-5">
            Bir aile sıcaklığında, klinik disipliniyle
          </h2>
          <p className="text-[#4B5A6E] leading-relaxed mb-4">
            MediEste, Ankara Bağlıca / Etimesgut&apos;ta 20 yılı aşkın klinik pratiğe sahip bir estetik &amp; güzellik merkezidir. Uzman Estetisyen Sevda Karataş ve Podolog Serdar Ceylan öncülüğündeki ekibimiz; cilt bakımından lazer epilasyona, medikal ayak bakımından kalıcı makyaja kadar geniş bir hizmet yelpazesini tek çatı altında sunuyor.
          </p>
          <p className="text-[#4B5A6E] leading-relaxed mb-8">
            Her seansımıza kısa bir bireysel değerlendirmeyle başlıyor; cilt tipiniz, ihtiyaçlarınız ve yaşam alışkanlıklarınıza göre size özel bir bakım planı çıkarıyoruz. Kliniğimiz Yaşamkent, Ümitköy, Çayyolu ve Alacaatlı çevresinden misafirlerimize hafta içi ve hafta sonu esnek saatlerde randevu sunar.
          </p>
          <div className="grid grid-cols-2 gap-3 md:gap-4 mb-8">
            {stats.map((s, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 rounded-2xl bg-[#F5F7FA] border border-[#E5EAF0]"
              >
                <div className="h-10 w-10 rounded-xl bg-white text-[#10B981] flex items-center justify-center shrink-0 shadow-sm">
                  <s.icon size={18} />
                </div>
                <div className="min-w-0">
                  <p className="font-display text-base text-[#0B1B33] font-semibold leading-tight">{s.label}</p>
                  <p className="text-xs text-[#7C8CA0] mt-0.5 leading-snug">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/hakkimizda"
              data-testid="services-about-more"
              className="inline-flex items-center gap-1.5 h-12 px-6 rounded-full bg-[#0E2B4F] text-white text-xs tracking-[0.18em] uppercase font-semibold hover:bg-[#143a68] transition-colors"
            >
              Hakkımızda&apos;yı Keşfet <ArrowRight size={14} />
            </Link>
            <Link
              to="/hakkimizda#ekibimiz"
              data-testid="services-about-team"
              className="inline-flex items-center gap-1.5 h-12 px-6 rounded-full border border-[#D1D9E4] text-[#0B1B33] text-xs tracking-[0.18em] uppercase font-semibold hover:border-[#10B981]/60 hover:text-[#10B981] transition-colors"
            >
              Ekibimizi Tanı
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryFilteredList({ activeKey, onBack }) {
  const cat = categories[activeKey];
  const cover = COVERS[activeKey];
  const list = useMemo(() => services.filter((s) => s.category === activeKey), [activeKey]);
  const other = CAT_ORDER.filter((k) => k !== activeKey);
  if (!cat) return null;

  return (
    <>
      <section className="pb-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm tracking-[0.14em] uppercase text-[#4F8B2A] hover:text-[#3F7A2E] font-semibold mb-6"
          data-testid="cat-back"
        >
          <ArrowLeft size={14} /> Tüm Kategoriler
        </button>
        <p className="chip-green mb-4">{cat.label}</p>
        <h1 className="font-display text-4xl md:text-5xl text-[#0B1B33] leading-[1.05] max-w-3xl mb-5 tracking-tight">
          {cat.label} Uygulamalarımız
        </h1>
        <p className="text-[#4B5A6E] max-w-2xl leading-relaxed text-base md:text-lg mb-3">
          {cover?.intro || cover?.tagline || cat.description}
        </p>
        {cover?.hub ? (
          <Link
            to={cover.hub}
            className="inline-flex items-center gap-1.5 text-sm text-[#4F8B2A] font-semibold underline underline-offset-4 hover:text-[#3F7A2E]"
          >
            {cat.label} hakkında detaylı bilgi →
          </Link>
        ) : null}
      </section>

      <section className="pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {list.map((s, i) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              data-testid={`service-list-${s.slug}`}
              className="group relative block overflow-hidden aspect-[3/4] rounded-2xl medi-card"
            >
              {s.image ? (
                <img
                  src={`/${s.image}`}
                  alt={s.title}
                  loading={i < 4 ? "eager" : "lazy"}
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                />
              ) : (
                <div className="w-full h-full bg-[#F5F7FA]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B33]/85 via-[#0B1B33]/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                <h3 className="font-display text-base md:text-lg text-white leading-tight mb-1 font-medium">{s.title}</h3>
                <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-[#A8C520] font-bold opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all">Keşfet <ArrowRight size={10} /></span>
              </div>
            </Link>
          ))}
        </div>
        {list.length === 0 ? (
          <p className="text-center text-[#7C8CA0] py-16">Bu kategoride henüz hizmet bulunmuyor.</p>
        ) : null}
      </section>

      {/* Diğer kategoriler mini navigasyon */}
      <section className="pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="chip mb-4">Diğer Kategoriler</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {other.map((k) => {
            const c = categories[k];
            const cv = COVERS[k];
            if (!c || !cv) return null;
            const Icon = cv.icon;
            return (
              <Link
                key={k}
                to={`/hizmetlerimiz?kategori=${k}`}
                data-testid={`other-cat-${k}`}
                className="group flex items-center gap-3 p-4 md:p-5 rounded-2xl bg-white border border-[#E5EAF0] hover:border-[#4F8B2A]/40 hover:shadow-md transition-all"
              >
                <div className="h-11 w-11 rounded-xl bg-[#6CA320]/10 text-[#4F8B2A] flex items-center justify-center shrink-0">
                  <Icon size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-sm md:text-base text-[#0B1B33] font-medium leading-tight truncate">{c.label}</p>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[#7C8CA0] mt-0.5 font-semibold">
                    {services.filter((s) => s.category === k).length} Hizmet →
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </>
  );
}

export default function ServicesList() {
  const [sp, setSp] = useSearchParams();
  const initial = sp.get("kategori");
  const [active, setActive] = useState(initial && CAT_ORDER.includes(initial) ? initial : null);

  const activateCat = (key) => {
    if (key && CAT_ORDER.includes(key)) {
      setActive(key);
      sp.set("kategori", key);
      setSp(sp, { replace: false });
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
    }
  };
  const clearCat = () => {
    setActive(null);
    sp.delete("kategori");
    setSp(sp, { replace: false });
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 30);
  };

  // Keep local state in sync with URL param when user hits browser back/forward.
  const katParam = sp.get("kategori");
  useEffect(() => {
    if (katParam && CAT_ORDER.includes(katParam)) {
      setActive((prev) => (prev !== katParam ? katParam : prev));
    } else {
      setActive((prev) => (prev !== null ? null : prev));
    }
  }, [katParam]);

  return (
    <>
      <SEO
        title={active ? `${categories[active]?.label} — Hizmetlerimiz` : "Hizmetlerimiz"}
        description="Ankara Bağlıca'da cilt bakımı, lazer epilasyon, kalıcı makyaj, medikal ayak bakımı (podoloji) ve bölgesel sıkılaşma hizmetlerimiz. MediEste'de kategoriye göre keşfet."
        path={active ? `/hizmetlerimiz?kategori=${active}` : "/hizmetlerimiz"}
      />
      <section className="pt-32 pb-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumb
          items={
            active
              ? [
                  { label: "Hizmetlerimiz", to: "/hizmetlerimiz" },
                  { label: categories[active]?.label || "Kategori" },
                ]
              : [{ label: "Hizmetlerimiz" }]
          }
        />
      </section>

      {active ? (
        <CategoryFilteredList activeKey={active} onBack={clearCat} />
      ) : (
        <>
          <PageHero />
          <CategoryQuickNav />
          <CategorySections onFilter={activateCat} />
        </>
      )}

      <QuickAppointmentBlock />
      <AboutUsBlock />
    </>
  );
}
